import React, { useState } from 'react'
import { useLocation } from 'wouter'
import * as UI from '@mantine/core'

import Logo from '../../resources/icons/logotype-bleu-fonce.png'

import { graphql } from '../../services/graphql'
import { buildComponent } from '../../components/factory'

// Écran « mot de passe oublié », deux modes selon la présence d'un ?token= :
// - sans token : on demande l'email → envoi du lien de réinitialisation.
// - avec token : on saisit le nouveau mot de passe.
export const ScreenDisconnectedPasswordReset = buildComponent()
  .withLifecycle(() => {
    const [, setLocation] = useLocation()

    const token = new URLSearchParams(window.location.search).get('token')

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [emailSent, setEmailSent] = useState(false)
    const [resetResult, setResetResult] = useState<{
      status: string
      error: string | null
    } | null>(null)
    const [done, setDone] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const requestReset = async () => {
      setError(null)
      setSubmitting(true)
      try {
        const { administratorResetPassword } =
          await graphql.queries.resetPassword(email.toLowerCase().trim())
        setResetResult(administratorResetPassword)
        setEmailSent(true)
      } catch (err) {
        setError('Une erreur est survenue. Réessayez.')
      } finally {
        setSubmitting(false)
      }
    }

    const setNewPassword = async () => {
      setError(null)

      if (password.length < 6) {
        setError('Le mot de passe doit faire au moins 6 caractères.')
        return
      }
      if (password !== confirm) {
        setError('Les mots de passe ne correspondent pas.')
        return
      }

      setSubmitting(true)
      try {
        const { administratorSetNewPassword } =
          await graphql.mutations.setNewPassword(token as string, password)

        if (administratorSetNewPassword) {
          setDone(true)
        } else {
          setError('Lien invalide ou expiré. Refaites une demande.')
        }
      } catch (err) {
        setError('Une erreur est survenue. Réessayez.')
      } finally {
        setSubmitting(false)
      }
    }

    return {
      token,
      email,
      setEmail,
      password,
      setPassword,
      confirm,
      setConfirm,
      submitting,
      emailSent,
      resetResult,
      done,
      error,
      requestReset,
      setNewPassword,
      goToLogin: () => setLocation('/'),
    }
  })
  .withRender(({ lifecycle }) => (
    <UI.Box
      sx={(theme) => ({
        width: '100%',
        // Même correctif que l'écran de connexion : les styles globaux de
        // Mantine figent html/body/#root à la hauteur de la fenêtre, donc
        // `height: 100%` bloquait cette boîte à cette taille et le contenu se
        // peignait par-dessus les bandes bleues, qui sont ses bordures. Cet
        // écran est celui où atterrit le lien d'invitation d'un nouvel
        // administrateur (#7512) : il porte un formulaire à deux mots de passe
        // et ses messages d'erreur.
        minHeight: '100%',
        borderTop: '4rem solid ' + theme.colors.blue[7],
        borderBottom: '8rem solid ' + theme.colors.blue[7],
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      })}
    >
      <UI.Container size={420}>
        <UI.Stack spacing={0} justify="center">
          <UI.Title align="center" title="Nap&Up">
            <UI.Image mx="auto" mb={32} width={128} src={Logo} alt="Nap&Up" />
          </UI.Title>

          <UI.Paper withBorder px={32} py={48} mt={16} radius="md">
            {lifecycle.token ? (
              // --- Mode : définir un nouveau mot de passe ---
              lifecycle.done ? (
                <UI.Stack>
                  <UI.Text align="center">
                    Votre mot de passe a été mis à jour.
                  </UI.Text>
                  <UI.Button fullWidth onClick={lifecycle.goToLogin}>
                    Se connecter
                  </UI.Button>
                </UI.Stack>
              ) : (
                <>
                  <UI.Title order={3} align="center" mb={24}>
                    Nouveau mot de passe
                  </UI.Title>
                  <UI.PasswordInput
                    label="Nouveau mot de passe"
                    disabled={lifecycle.submitting}
                    value={lifecycle.password}
                    onChange={(e) =>
                      lifecycle.setPassword(e.currentTarget.value)
                    }
                  />
                  <UI.PasswordInput
                    label="Confirmer le mot de passe"
                    mt="md"
                    disabled={lifecycle.submitting}
                    value={lifecycle.confirm}
                    onChange={(e) =>
                      lifecycle.setConfirm(e.currentTarget.value)
                    }
                  />
                  {lifecycle.error && (
                    <UI.Alert mt="md" color="red">
                      <UI.Text color="red">{lifecycle.error}</UI.Text>
                    </UI.Alert>
                  )}
                  <UI.Button
                    fullWidth
                    mt="xl"
                    loading={lifecycle.submitting}
                    onClick={lifecycle.setNewPassword}
                  >
                    Valider
                  </UI.Button>
                </>
              )
            ) : // --- Mode : demander la réinitialisation ---
            lifecycle.emailSent ? (
              <UI.Stack>
                {lifecycle.resetResult?.status === 'sent' && (
                  <UI.Text align="center">
                    Un email avec un lien de réinitialisation vient d'être
                    envoyé à cette adresse.
                  </UI.Text>
                )}
                {lifecycle.resetResult?.status === 'account_not_found' && (
                  <UI.Alert color="orange">
                    <UI.Text>
                      [DEV] Aucun compte n'existe pour cette adresse. Créez
                      d'abord un compte d'accès avec cet email exact.
                    </UI.Text>
                  </UI.Alert>
                )}
                {lifecycle.resetResult?.status === 'email_error' && (
                  <UI.Alert color="red">
                    <UI.Text color="red">
                      [DEV] L'envoi de l'email a échoué :{' '}
                      {lifecycle.resetResult.error}
                    </UI.Text>
                  </UI.Alert>
                )}
                <UI.Anchor align="center" onClick={lifecycle.goToLogin}>
                  Retour à la connexion
                </UI.Anchor>
              </UI.Stack>
            ) : (
              <>
                <UI.Title order={3} align="center" mb={24}>
                  Mot de passe oublié
                </UI.Title>
                <UI.TextInput
                  label="Email"
                  type="email"
                  disabled={lifecycle.submitting}
                  value={lifecycle.email}
                  onChange={(e) => lifecycle.setEmail(e.currentTarget.value)}
                />
                {lifecycle.error && (
                  <UI.Alert mt="md" color="red">
                    <UI.Text color="red">{lifecycle.error}</UI.Text>
                  </UI.Alert>
                )}
                <UI.Button
                  fullWidth
                  mt="xl"
                  loading={lifecycle.submitting}
                  onClick={lifecycle.requestReset}
                >
                  Envoyer le lien
                </UI.Button>
                <UI.Anchor
                  align="center"
                  mt="md"
                  size="sm"
                  sx={{ display: 'block' }}
                  onClick={lifecycle.goToLogin}
                >
                  Retour à la connexion
                </UI.Anchor>
              </>
            )}
          </UI.Paper>
        </UI.Stack>
      </UI.Container>
    </UI.Box>
  ))
